import { useEffect, useRef, type Ref } from 'react'
import styled from 'styled-components'
import { ArrowRightIcon, NegativeCircleIcon } from '@shipt/design-system-icons'
import { type PillInputProps, type WrapperProps } from '@/features/shared/elements/Inputs/types'
import {
  Body1FontStyles,
  InputContainer,
  HelperMessagesGroup,
  HelperText,
  InputElement,
} from '@/features/shared/elements/Inputs/styles'
import { Row } from '@shipt/design-system-layouts'
import { TransparentButton } from '@/features/shared/elements/Buttons'

type Props = PillInputProps & { ref?: Ref<HTMLInputElement> }

export const PillInput = ({
  id,
  label,
  type,
  onFocus,
  onBlur,
  onChange,
  handleClear,
  onKeyDown,
  onSubmit,
  value,
  error = '',
  name,
  maxLength,
  ariaLabel,
  autoComplete,
  inputMode,
  icon: Icon,
  iconLabel = 'SVG',
  hasSubmitButton = true,
  showError = true,
  autoFocus,
  surface = 'default',
  isGrayBackground = false,
  ref,
}: Props) => {
  const isDisabled = value?.length !== maxLength
  const buttonLabel = isDisabled ? 'Button disabled' : 'Button enabled'
  const errorId = `${id}-error`
  const hasError = Boolean(error)
  const componentRef = useRef<HTMLInputElement | null>(null)
  const inputRef = ref || componentRef

  useEffect(() => {
    if (typeof inputRef !== 'function' && autoFocus) {
      const timeout = setTimeout(() => {
        inputRef.current?.focus()
      }, 50)
      return () => clearTimeout(timeout)
    }
  }, [autoFocus, inputRef])

  const handleOnClickIcon = () => {
    if ('current' in inputRef) {
      inputRef.current?.focus()
    }
  }
  const inputProps = {
    id,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    'aria-label': ariaLabel,
    value,
    maxLength,
    name,
    autoComplete,
    inputMode,
    ...(error && {
      hasError: true,
      'aria-invalid': true,
      'aria-describedby': errorId,
    }),
  }

  return (
    <InputContainer>
      <ContentWrapper
        hasError={hasError}
        hasImage={Boolean(Icon)}
        surface={surface}
        isGray={isGrayBackground}
      >
        {!!Icon && <Icon aria-label={iconLabel} onClick={handleOnClickIcon} />}
        <InputBorderWrapper>
          <Input {...inputProps} placeholder={label} type={type} ref={inputRef} />
        </InputBorderWrapper>
        {Boolean(value) && (
          <Row>
            {!!handleClear && (
              <ResetButton type="reset" onClick={handleClear} aria-label="reset">
                <NegativeCircleIcon size="sm" />
              </ResetButton>
            )}
            {!!hasSubmitButton && (
              <RoundButton
                disabled={isDisabled}
                aria-label={buttonLabel}
                type="submit"
                onClick={onSubmit}
              >
                <ArrowRightIcon size="sm" />
              </RoundButton>
            )}
          </Row>
        )}
      </ContentWrapper>
      {!!showError && !!hasError && (
        <HelperContainer>
          <HelperText aria-label={errorId} id={errorId} hasError surface={surface}>
            {error}
          </HelperText>
        </HelperContainer>
      )}
    </InputContainer>
  )
}

const RoundButton = styled(TransparentButton)`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background-color: ${({ theme }) => theme.primary};

  svg {
    color: ${({ theme }) => theme.gray300};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.gray300};

    svg {
      color: ${({ theme }) => theme.gray600};
    }
  }
`

const Input = styled(InputElement)`
  background-color: inherit;
`

const ContentWrapper = styled.div<WrapperProps & { isGray: boolean }>`
  grid-template-columns: ${({ hasImage }) => (hasImage ? 'auto 1fr auto' : '1fr auto')};
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 0 6px 0 16px;
  min-height: 48px;
  border-radius: 64px;
  background: ${({ theme, isGray }) => (isGray ? theme.gray100 : theme.white)};

  &:focus-within {
    background: ${({ theme, isGray }) => isGray && theme.white};
    border: 2px solid;
  }

  border: 1px solid
    ${({ theme, hasError, surface, isGray }) => {
      if (hasError) return surface === 'default' ? theme.red500 : theme.red400
      if (isGray) return theme.gray100
      return theme.plum
    }};
  box-sizing: border-box;
`

const InputBorderWrapper = styled.div`
  ${Body1FontStyles};
  position: relative;
  cursor: text;

  &:focus-within label {
    color: ${({ theme }) => theme.gray400};
  }
`
const ResetButton = styled(TransparentButton)`
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
`

const HelperContainer = styled(HelperMessagesGroup)`
  padding-left: 1rem;
`
